import { Component, DestroyRef, HostListener, signal, ViewEncapsulation, inject, OnInit, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MenuItem } from 'primeng/api';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '../service/layout.service';
import { MenubarModule } from 'primeng/menubar';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { TooltipModule } from 'primeng/tooltip';
import { Button, ButtonModule } from 'primeng/button';
import { LinksService } from 'src/app/services/links.service';
import { Link } from 'src/app/interfaces/links.interface';
import { ListResponse } from 'src/app/interfaces/responses.interface';
import { UsuarioService } from 'src/app/services/auth/usuario.service';
import {
  TopbarCmsData,
  normalizarTopbarCmsData,
  obtenerTopbarCmsPorDefecto
} from 'src/app/interfaces/topbar-cms.interface';
import { TopbarService } from 'src/app/services/topbar.service';
import { isValid } from 'src/app/utils/code-utils';
import { Subscription } from 'rxjs';

/**
 * Componente principal de barra superior del layout.
 *
 * Flujo: carga menú dinámico y configuración CMS del topbar para renderizar
 * información de contacto y redes sociales desde backend.
 *
 * Propósito: unificar navegación y datos institucionales en un encabezado dinámico.
 */
@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    StyleClassModule,
    MenubarModule,
    TieredMenuModule,
    TooltipModule,
    ButtonModule
  ],
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      .layout-topbar .layout-topbar-logo-container {
        // width: 8rem;
      }
      .layout-topbar {
        top: 35px !important;
      }
      .custom-p-menubar {
        .p-menubar {
          // background-color: var(--cnb-blue);
          background: transparent;
          border: none;
          padding: 0.75rem 0;
          max-height: 3rem;

          .p-menubar-item-icon {
            color: var(--p-menubar-color) !important;
          }

          .p-menubar-item-link:hover,
          .p-menubar-item-active > .p-menubar-item-content,
          .p-menubar-item:not(.p-disabled) > .p-menubar-item-content:hover,
          .p-menubar-item.p-focus > .p-menubar-item-content {
            // background-color: #505d7d !important;
            // border-radius: 0.5rem;
            .p-menubar-item-label,
            .p-menubar-item-icon,
            .p-menubar-submenu-icon > .p-icon {
              // color: var(--primary-contrast-color) !important;
            }
          }

          // :active {
          //   background-color: #505d7d !important;
          //   border-radius: 0.5rem;
          // }

          .p-menubar-submenu {
            .p-menubar-item-label,
            .p-menubar-item-icon,
            .p-menubar-submenu-icon > .p-icon {
              color: var(--p-menubar-color) !important;
            }
          }
        }
      }

      .p-menubar-item:not(.p-disabled) .p-menubar-item-content {
        color: var(--p-menubar-item-color) !important;
        background: transparent !important;
        &:active {
          color: var(--p-menubar-item-focus-background) !important;
        }
        &:hover {
          color: var(--p-menubar-item-focus-background) !important;
        }
      }

      .account-menu-trigger {
        display: flex;
        align-items: center;
        gap: 0.65rem;
        min-height: 2.75rem;
        padding: 0.35rem 0.55rem 0.35rem 0.4rem;
        border: 1px solid var(--surface-border);
        border-radius: 999px;
        background: var(--surface-card);
        color: var(--text-color);
        cursor: pointer;
        transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
      }

      .account-menu-trigger:hover {
        border-color: var(--primary-color);
        box-shadow: 0 8px 22px color-mix(in srgb, var(--primary-color) 16%, transparent);
        transform: translateY(-1px);
      }

      .account-menu-avatar {
        display: grid;
        place-items: center;
        width: 2rem;
        height: 2rem;
        border-radius: 50%;
        color: var(--primary-contrast-color);
        background: var(--primary-color);
        font-size: 0.78rem;
        font-weight: 800;
      }

      .account-menu-copy {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        line-height: 1.1;
      }

      .account-menu-copy strong { font-size: 0.82rem; }
      .account-menu-copy small { margin-top: 0.2rem; color: var(--text-color-secondary); font-size: 0.68rem; }

      .admin-account-menu.p-tieredmenu {
        width: 18.5rem;
        padding: 0.5rem;
        border-radius: 12px;
        box-shadow: 0 18px 45px rgba(15, 23, 42, 0.18);
      }

      .admin-access-button {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        min-height: 44px;
        padding: 0 1rem;
        border-radius: 999px;
        background: var(--ui-primary-solid);
        color: var(--primary-contrast-color);
        font-size: 0.875rem;
        font-weight: 700;
        text-decoration: none;
        white-space: nowrap;
        transition: box-shadow 0.2s ease, transform 0.2s ease;
      }
      .admin-access-button:hover {
        box-shadow: 0 8px 22px color-mix(in srgb, var(--primary-color) 30%, transparent);
        transform: translateY(-1px);
      }
      .admin-access-button:focus-visible {
        outline: 3px solid color-mix(in srgb, var(--primary-color) 55%, transparent);
        outline-offset: 3px;
      }
      .admin-access-button.is-current { box-shadow: inset 0 0 0 2px var(--primary-contrast-color); }

      @media (max-width: 760px) {
        .admin-access-button { width: 44px; justify-content: center; padding: 0; }
        .admin-access-label { display: none; }
        .account-menu-copy { display: none; }
        .account-menu-trigger { padding-right: 0.4rem; }
      }
    `
  ],
  template: `
    @if (mostrarFranjaSuperior()) {
      <div class="public-info-strip h-[35px] flex row items-center px-6 justify-between fixed top-0 w-full z-[100]">
        <div class="flex items-center gap-3 overflow-hidden">
          @if (mostrarTelefono()) {
            <a class="public-contact-link contact-phone" [href]="'tel:' + topbarConfig().telefono.numero">
              <i class="fa fa-phone-alt mr-2"></i>
              <small>{{ topbarConfig().telefono.numero }}</small>
            </a>
          }
          @if (mostrarCorreo()) {
            <a class="public-contact-link contact-email" [href]="'mailto:' + topbarConfig().correo.email">
              <i class="fa fa-envelope mr-2"></i>
              <small>{{ topbarConfig().correo.email }}</small>
            </a>
          }
          @if (mostrarDireccion()) {
            <a class="public-contact-link contact-address hidden md:inline-flex" [href]="direccionUrl()" target="_blank" rel="noopener noreferrer">
              <i class="fa fa-map-marker-alt mr-2"></i>
              <small>{{ topbarConfig().direccion.texto }}</small>
            </a>
          }
        </div>

        @if (mostrarWhatsapp() || redesVisibles().length) {
          <div class="text-right flex items-center">
            @if (mostrarWhatsapp()) {
              <a class="text-primary px-2" [href]="whatsappUrl()" target="_blank" rel="noopener noreferrer" aria-label="Contactar por WhatsApp">
                <i class="fab fa-whatsapp"></i>
              </a>
            }
            @if (topbarConfig().redes.visible && redesVisibles().length) {
            <div class="flex row items-center">
              @for (red of redesVisibles(); track red.nombre; let ultimo = $last) {
                <a
                  class="text-primary"
                  [class.px-2]="!ultimo"
                  [class.pl-2]="ultimo"
                  [href]="red.url"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i [class]="red.iconoClase"></i>
                </a>
              }
            </div>
            }
          </div>
        }
      </div>
    }
    <div
      class="layout-topbar topbar-glass !flex !items-center !flex-row !h-[6rem]"
      [class.no-public-strip]="!mostrarFranjaSuperior()"
      [style.border-bottom]="
        layoutService.isDarkTheme() ? '1px solid var(--p-surface-950)' : '1px solid var(--p-surface-100)'
      "
    >
      <div class="layout-topbar-logo-container flex items-center justify-center !w-max">
        @if (!showMenuBar()) {
          <button class="layout-menu-button layout-topbar-action" (click)="layoutService.onMenuToggle()">
            <i class="pi pi-bars"></i>
          </button>
        }
        <a class="layout-topbar-logo w-fit" routerLink="/">
          <div class="my-0 mr-6 w-max flex items-center" [style.font-size]="'1.75rem'" aria-label="Inicio">
            @for (segment of topbarConfig().titulo || []; track $index) {
              @if (segment.esPrimario) {
                <b class="text-primary whitespace-pre">{{ segment.texto }}</b>
              } @else {
                <span class="whitespace-pre">{{ segment.texto }}</span>
              }
            }
          </div>
        </a>
      </div>

      @if (showMenuBar()) {
        @for (item of items(); track item; let i = $index) {
          <p-menubar class="custom-p-menubar" [model]="item" [breakpoint]="getBreakpoint(i)" />
        }
      }

      <div class="layout-topbar-actions">
        @if (isAdminUser()) {
          <a
            routerLink="/admin"
            class="admin-access-button"
            aria-label="Ir al panel de administración"
            [attr.aria-current]="enAdmin() ? 'page' : null"
            [class.is-current]="enAdmin()"
          >
            <i class="pi pi-shield" aria-hidden="true"></i>
            <span class="admin-access-label">Administración</span>
          </a>
        }

        <button
          class="layout-topbar-menu-button layout-topbar-action"
          pStyleClass="@next"
          enterFromClass="hidden"
          enterActiveClass="animate-scalein"
          leaveToClass="hidden"
          leaveActiveClass="animate-fadeout"
          [hideOnOutsideClick]="true"
          [pTooltip]="'Opciones'"
          tooltipPosition="left"
        >
          <i class="pi pi-ellipsis-v"></i>
        </button>

        <div class="layout-topbar-menu hidden lg:block">
          <div class="layout-topbar-menu-content">
            <!-- <button type="button" class="layout-topbar-action">
            <i class="pi pi-calendar"></i>
            <span>Calendar</span>
          </button>
          <button type="button" class="layout-topbar-action">
            <i class="pi pi-inbox"></i>
            <span>Messages</span>
          </button> -->
            <!-- <button
              type="button"
              class="layout-topbar-action"
              [pTooltip]="layoutService.isMobile() ? '' : 'Información de la cuenta'"
              tooltipPosition="left"
            >
              <i class="pi pi-user"></i>
              <span>Informacion de la Cuenta</span>
            </button> -->

            @if (isLoggedIn()) {
              <p-tieredmenu
                #accountMenu
                [model]="accountMenuItems"
                [popup]="true"
                appendTo="body"
                styleClass="admin-account-menu"
              />
              <button
                type="button"
                class="account-menu-trigger"
                (click)="accountMenu.toggle($event)"
                aria-label="Abrir menú de cuenta"
              >
                <span class="account-menu-avatar">{{ userInitials() }}</span>
                <span class="account-menu-copy">
                  <strong>{{ userName() }}</strong>
                  <small>{{ userRoleLabel() }}</small>
                </span>
                <i class="pi pi-chevron-down text-xs"></i>
              </button>
            }
            <!-- @else {
              <button
                type="button"
                pButton
                class="layout-topbar-action-highlight"
                [pTooltip]="layoutService.isMobile() ? '' : 'Iniciar sesión'"
                [label]="'Iniciar sesión'"
                tooltipPosition="left"
                [routerLink]="['/auth/login']"
              >
                <i class="pi pi-user"></i>
              </button>
            } -->
          </div>
        </div>
      </div>
    </div>
  `
})
export class AppTopbar implements OnInit {
  private destroyRef = inject(DestroyRef);
  /**
   * Items del menú cargados dinámicamente desde el servicio
   */
  items = signal<MenuItem[][]>([]);
  topbarConfig = signal<TopbarCmsData>(obtenerTopbarCmsPorDefecto());
  redesVisibles = computed(() => this.topbarConfig().redes.items.filter((red) => red.visible && !!red.url?.trim()));
  mostrarTelefono = computed(() => this.topbarConfig().telefono.visible && !!this.topbarConfig().telefono.numero?.trim());
  mostrarCorreo = computed(() => this.topbarConfig().correo.visible && !!this.topbarConfig().correo.email?.trim());
  mostrarWhatsapp = computed(() => this.topbarConfig().whatsapp.visible && !!this.topbarConfig().whatsapp.numero?.trim());
  mostrarDireccion = computed(() => this.topbarConfig().direccion.visible && !!this.topbarConfig().direccion.texto?.trim());
  mostrarFranjaSuperior = computed(() => this.topbarConfig().visible && (
    this.mostrarTelefono() || this.mostrarCorreo() || this.mostrarWhatsapp() || this.mostrarDireccion() ||
    (this.topbarConfig().redes.visible && this.redesVisibles().length > 0)
  ));
  whatsappUrl = computed(() => {
    const config = this.topbarConfig().whatsapp;
    const numero = config.numero.replace(/\D/g, '');
    return `https://wa.me/${numero}?text=${encodeURIComponent(config.mensaje || '')}`;
  });
  direccionUrl = computed(() => {
    const config = this.topbarConfig().direccion;
    return config.urlMapa?.trim() || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(config.texto)}`;
  });
  tituloFontSize = computed(() => {
    const config = this.topbarConfig();
    if (!config.titulo) return '1.5rem';

    // Concatenamos todo el texto para saber su longitud total
    const fullText = config.titulo.map(t => t.texto).join('');
    const charCount = fullText.length;
    
    // Heurística simple: 
    // Ancho base disponible aprox 128px (8rem).
    // Si hay pocos caracteres (ej: 10), font-size puede ser grande (hasta 1.75rem).
    // Si hay muchos (ej: 20+), reducimos.
    
    if (charCount <= 6) return '1.75rem';
    if (charCount <= 10) return '1.5rem';
    if (charCount <= 15) return '1.1rem';
    if (charCount <= 20) return '0.9rem';
    return '0.75rem'; // Fallback para textos muy largos
  });

  usuarioService = inject(UsuarioService);
  private topbarService = inject(TopbarService);
  private router = inject(Router);
  private menuRequest?: Subscription;
  private accountMenuRole = '';
  private accountMenuItemsCache: MenuItem[] = [];

  get accountMenuItems(): MenuItem[] {
    const role = this.getStoredUserRole();
    if (role === this.accountMenuRole && this.accountMenuItemsCache.length > 0) {
      return this.accountMenuItemsCache;
    }

    const isAdmin = role === 'ADMIN';
    this.accountMenuRole = role;
    this.accountMenuItemsCache = [
    {
      label: 'Gestión operativa',
      icon: 'pi pi-chart-line',
      items: [
        { label: 'Dashboard operativo', icon: 'pi pi-th-large', routerLink: ['/admin/operativa'] },
        { label: 'Agenda y reservas', icon: 'pi pi-calendar', routerLink: ['/reservas'] },
        { label: 'Servicios', icon: 'pi pi-briefcase', routerLink: ['/admin/servicios'], visible: isAdmin },
        { label: 'Clientes', icon: 'pi pi-address-book', routerLink: ['/clientes'] },
        { label: 'Usuarios y accesos', icon: 'pi pi-users', routerLink: ['/usuarios'], visible: isAdmin },
        { label: isAdmin ? 'Personal' : 'Profesionales', icon: 'pi pi-id-card', routerLink: [isAdmin ? '/admin/empleados' : '/empleados'] }
      ]
    },
    {
      label: 'Diseño de página',
      icon: 'pi pi-palette',
      visible: isAdmin,
      items: [
        { label: 'Dashboard de diseño', icon: 'pi pi-th-large', routerLink: ['/admin/diseno'] },
        { label: 'Portada', icon: 'pi pi-home', routerLink: ['/admin/home'] },
        { label: 'Colores y estilo', icon: 'pi pi-sliders-h', routerLink: ['/admin/diseno/apariencia'] },
        { label: 'Menús y enlaces', icon: 'pi pi-sitemap', routerLink: ['/admin/links'] },
        { label: 'Páginas', icon: 'pi pi-file-edit', routerLink: ['/admin/paginas'] },
        { label: 'Catálogo de servicios', icon: 'pi pi-briefcase', routerLink: ['/admin/servicios'], queryParams: { view: 'publication' } }
      ]
    },
    { label: 'Configuración general', icon: 'pi pi-cog', routerLink: ['/admin/configuracion'], visible: isAdmin },
    { separator: true },
    { label: 'Panel de administración', icon: 'pi pi-cog', routerLink: ['/admin'], visible: isAdmin },
    { label: 'Ver sitio público', icon: 'pi pi-external-link', routerLink: ['/home'] },
    { separator: true },
    { label: 'Cerrar sesión', icon: 'pi pi-power-off', command: () => this.logout() }
    ];
    return this.accountMenuItemsCache;
  }

  private getStoredUserRole(): string {
    try {
      return JSON.parse(localStorage.getItem('usuario') ?? '{}')?.rol ?? '';
    } catch {
      return '';
    }
  }

  constructor(
    public layoutService: LayoutService,
    private linksService: LinksService
  ) {}

  showMenuBar = signal<boolean>(false);

  /**
   * Transforma un array de enlaces en la estructura de menú requerida por PrimeNG
   * @param links Array de enlaces obtenidos del servicio
   * @returns Array bidimensional de MenuItem para el menubar
   */
  private transformarEnlacesAMenu(links: Link[]): MenuItem[][] {
    const menuItems: MenuItem[] = [];

    links.forEach((link) => {
      // Filtrar páginas visibles con ruta válida
      const paginasVisibles = link.paginas?.filter((pagina) => pagina.visible === 'SI' && pagina.route) || [];
      const tieneHijos = link.hijos && link.hijos.length > 0;

      // Caso 1: Si no tiene hijos y no tiene páginas visibles, no mostrar el link
      if (!tieneHijos && paginasVisibles.length === 0) {
        return; // No agregamos este link
      }

      // Caso 2: Si no tiene hijos pero tiene exactamente una página, mostrar la página directamente
      if (!tieneHijos && paginasVisibles.length === 1) {
        const pagina = paginasVisibles[0];
        const menuItem: MenuItem = {
          label: pagina.nombre,
          routerLink: [pagina.route!]
        };
        menuItems.push(menuItem);
        return;
      }

      // Caso normal: Crear menú con subitems
      const menuItem: MenuItem = {
        label: link.label
      };

      // Agregar páginas como items del menú
      if (paginasVisibles.length > 0) {
        menuItem.items = paginasVisibles.map((pagina) => ({
          label: pagina.nombre,
          routerLink: [pagina.route!]
        }));
      }

      // Si tiene hijos, procesarlos recursivamente
      if (tieneHijos) {
        const subMenuItems = this.transformarEnlacesAMenu(link.hijos!);
        if (subMenuItems.length > 0) {
          const subItems = subMenuItems.flat();
          if (menuItem.items) {
            menuItem.items = [...menuItem.items, ...subItems];
          } else {
            menuItem.items = subItems;
          }
        }
      }

      // Solo agregamos el item si tiene contenido
      if (menuItem.items && menuItem.items.length > 0) {
        menuItems.push(menuItem);
      }
    });

    let newMenuItems: MenuItem[][] = [];
    for (const item of menuItems) {
      newMenuItems.push([item]);
    }

    // Retornamos como array bidimensional para mantener compatibilidad con el template
    return newMenuItems.length > 0 ? newMenuItems : [];
  }

  /**
   * Inicializa el estado del componente al cargar el layout.
   *
   * Flujo: calcula breakpoint actual, carga el menú principal y obtiene
   * la configuración dinámica del topbar desde backend.
   *
   * @returns `void`.
   */
  ngOnInit() {
    this.linksService.menuChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cargarMenu());
    this.getScreenSize();
    this.cargarMenu();
    this.cargarTopbar();
  }

  isLoggedIn() {
    let token = localStorage.getItem('token')!;
    if (!!token && token !== 'undefined' && token !== 'null') {
      return true;
    }
    return false;
  }

  /**
   * Carga los elementos del menú desde el servicio de enlaces
   */
  private cargarMenu(): void {
    this.menuRequest?.unsubscribe();
    this.menuRequest = this.linksService.arbol().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: ListResponse<Link>) => {
        if (!isValid(response.code) || !Array.isArray(response.data)) return;
        const linksOrdenados = response.data.sort((a, b) => {
          // Si ambos tienen orden, comparar por orden
          if (a.orden !== undefined && b.orden !== undefined) {
            if (a.orden !== b.orden) {
              return a.orden - b.orden;
            }
            // Si el orden es igual, comparar por id
            return (a.id || 0) - (b.id || 0);
          }

          // Si uno tiene orden y el otro no, priorizar el que tiene orden (o definir criterio)
          // Asumiremos que null/undefined va al final o principio.
          // Si queremos que los que no tienen orden vayan al final:
          if (a.orden !== undefined) return -1;
          if (b.orden !== undefined) return 1;

          // Si ninguno tiene orden, ordenar por id
          return (a.id || 0) - (b.id || 0);
        });

        const menuItems = this.transformarEnlacesAMenu(linksOrdenados);
        this.items.set(menuItems);
      },
      error: (error) => {
        console.error('Error al cargar el menú:', error);
        // En caso de error, mantenemos el menú vacío o podríamos mostrar un mensaje
      }
    });
  }

  /**
   * Carga la configuración dinámica de la barra superior.
   *
   * Flujo: consulta API, valida respuesta y normaliza la data para render seguro.
   *
   * @returns `void`.
   */
  private cargarTopbar(): void {
    this.topbarService.get().subscribe({
      next: (response) => {
        if (!isValid(response.code) || !response.data?.data) {
          this.topbarConfig.set(obtenerTopbarCmsPorDefecto());
          return;
        }

        this.topbarConfig.set(normalizarTopbarCmsData(response.data.data));
      },
      error: () => {
        this.topbarConfig.set(obtenerTopbarCmsPorDefecto());
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  getScreenSize(event?) {
    if (window.innerWidth > 1024) {
      this.showMenuBar.set(true);
    } else {
      this.showMenuBar.set(false);
    }
  }

  getBreakpoint(index: number) {
    const currentItems = this.items();
    if (currentItems.length === 0) {
      return '800px'; // Valor por defecto si no hay items
    }

    let widthAvailable = 800;
    let w60 = widthAvailable * 0.6;
    let w40 = widthAvailable * 0.4;
    let widthPartition = 100 / currentItems.length;
    let indexPartition = (index + 1) * widthPartition;
    let breakpoint = `${(indexPartition / 100) * w40 + w60}px`;
    // console.log(`the breakpoint for the index ${index} is ${breakpoint}`);
    return breakpoint;
  }

  reloadPage() {
    window.location.reload();
  }

  userName(): string {
    try {
      const user = JSON.parse(localStorage.getItem('usuario') ?? '{}');
      return user?.username || user?.nombre || 'Administrador';
    } catch {
      return 'Administrador';
    }
  }

  enAdmin(): boolean {
    return this.router.url.startsWith('/admin');
  }

  isAdminUser(): boolean {
    return this.getStoredUserRole() === 'ADMIN';
  }

  userRoleLabel(): string {
    try {
      const role = JSON.parse(localStorage.getItem('usuario') ?? '{}')?.rol;
      if (role === 'EMPLEADO') return 'Gestión operativa';
      if (role === 'ADMIN') return 'Administración';
      return 'Cuenta';
    } catch {
      return 'Cuenta';
    }
  }

  userInitials(): string {
    return this.userName()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || 'AD';
  }

  logout(): void {
    this.usuarioService.logout();
    this.router.navigate(['/home']);
  }
}
